(() => {
class YotpoSubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.hideCookieName = 'hide_subscription_widget';
    this.subscribeOption = null;
    this.oneTimeOption = null;
    this.sellingPlanInput = null;
    this.planButtons = [];
    this.subscriptionElement = null;
    this.subscriptionBlock = null;
    this.bundleVariantButtons = [];
    this.bundleQuantityList = null;
    this.bundleSliderPrev = null;
    this.bundleSliderNext = null;
    this.boundBundleSliderScrollHandler = null;
    this.boundBundleSliderResizeHandler = null;
    this.forceOneTime = false;
    this.isInitialized = false;
  }

  connectedCallback() {
    // Check manual hide cookie to prevent visual jumping
    if (this.shouldHideWidget()) {
      this.style.display = 'none';
      this.setLoadingState(false);
      // Ensure one-time option is selected when widget is hidden
      requestAnimationFrame(() => {
        this.selectOneTimeOption();
      });
      return;
    }

    this.setLoadingState(true);

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.init();
    });
  }

  init() {
    if (this.isInitialized) return;
    
    this.cacheElements();
    this.applyBundleSelectionFromUrl();
    this.bindEvents();
    
    // Check for promo and set default selection
    this.setInitialSelection();
    this.checkActiveOption();
    
    this.isInitialized = true;
    requestAnimationFrame(() => {
      this.setLoadingState(false);
    });
  }

  disconnectedCallback() {
    if (this.bundleSliderPrev && this.boundBundleSliderPrevHandler) {
      this.bundleSliderPrev.removeEventListener('click', this.boundBundleSliderPrevHandler);
    }

    if (this.bundleSliderNext && this.boundBundleSliderNextHandler) {
      this.bundleSliderNext.removeEventListener('click', this.boundBundleSliderNextHandler);
    }

    if (this.bundleQuantityList && this.boundBundleSliderScrollHandler) {
      this.bundleQuantityList.removeEventListener('scroll', this.boundBundleSliderScrollHandler);
    }

    if (this.boundBundleSliderResizeHandler) {
      window.removeEventListener('resize', this.boundBundleSliderResizeHandler);
    }

    this.setLoadingState(false);
  }

  setLoadingState(isLoading) {
    this.classList.toggle('yotpo-subscription-widget--loading', isLoading);
    this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  setInitialSelection() {
    // If there's an active promo, default to one-time purchase
    if (this.hasActivePromo()) {
      this.selectOneTimeOption();
    }
  }

  shouldHideWidget() {
    // Only check manual hide cookie (not promo status)
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(`${this.hideCookieName}=`))
      ?.split('=')[1] === 'true';
  }

  hasActivePromo() {
      // If there's an active promo, default to one-time purchase
      let productInfo = document.querySelector('product-info');
    // Check if promo tracker is available and has active promo
    if (typeof window.promoTracker !== 'undefined' && window.promoTracker && productInfo?.hasAttribute('data-default-otp')) {
      try {
        const promoStatus = window.promoTracker.getPromoStatus();
        return promoStatus.hasPromo;
      } catch (error) {
        console.warn('YotpoSubscriptionWidget: Error checking promo status', error);
        return false;
      }
    }
    return false;
  }

  selectOneTimeOption() {
    const oneTimeOption = this.querySelector('.purchase_option_one-time');
    const sellingPlanInput = document.querySelector('.selling_plan_input');
    
    if (oneTimeOption) {
      oneTimeOption.checked = true;
      if (sellingPlanInput) {
        sellingPlanInput.value = '';
      }
      
      // Log when one-time is selected due to promo
      if (this.hasActivePromo()) {
        console.log('[YotpoSubscriptionWidget] One-time purchase selected - active promo detected');
      }
      
      // Update content to reflect one-time selection
      this.updateWidgetContent();
    }
  }

  cacheElements() {
    this.subscribeOption = this.querySelector('.purchase_option_subscribe');
    this.oneTimeOption = this.querySelector('.purchase_option_one-time');
    this.sellingPlanInput = document.querySelector('.selling_plan_input');
    this.planButtons = Array.from(this.querySelectorAll('.subscription__plan-button'));
    this.subscriptionElement = this.querySelector('.block__element--subscription');
    this.subscriptionBlock = this.querySelector('.product-subscription__block');
    this.bundleVariantButtons = Array.from(this.querySelectorAll('[data-bundle-variant-option]'));
    this.bundleQuantityList = this.querySelector('[data-bundle-quantity-list]') || this.querySelector('.bundle-quantity-switcher__list');
    this.bundleSliderPrev = this.querySelector('[data-bundle-slider-prev]');
    this.bundleSliderNext = this.querySelector('[data-bundle-slider-next]');
    this.forceOneTime = this.shouldForceOneTimeFromBundleSwitcher();
  }

  shouldForceOneTimeFromBundleSwitcher() {
    const activeBundleOption = this.querySelector('[data-bundle-variant-option][aria-pressed="true"]');
    return activeBundleOption?.dataset.forceOneTime === 'true';
  }

  applyForceOneTimeMode() {
    if (this.subscriptionBlock) {
      this.subscriptionBlock.classList.toggle('product-subscription__block--one-time-only', this.forceOneTime);
    }

    if (this.subscriptionElement) {
      this.subscriptionElement.classList.toggle('block__element--disabled', this.forceOneTime);
      this.subscriptionElement.setAttribute('aria-disabled', this.forceOneTime ? 'true' : 'false');
    }

    if (this.subscribeOption) {
      this.subscribeOption.disabled = this.forceOneTime;
      if (this.forceOneTime) {
        this.subscribeOption.checked = false;
      }
    }

    if (this.oneTimeOption && this.forceOneTime) {
      this.oneTimeOption.checked = true;
    }

    if (this.sellingPlanInput && this.forceOneTime) {
      this.sellingPlanInput.value = '';
    }

    this.planButtons.forEach((button) => {
      button.disabled = this.forceOneTime;
    });
  }

  bindEvents() {
    this.bundleVariantButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleBundleVariantClick(e));
    });

    this.initBundleQuantitySlider();
    this.initBundleQuantityTip();

    if (!this.subscribeOption || !this.oneTimeOption) return;

    // Plan button handlers
    this.planButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handlePlanClick(e));
    });

    // Purchase option handlers
    this.subscribeOption.addEventListener('change', () => this.checkActiveOption());
    this.oneTimeOption.addEventListener('change', () => this.checkActiveOption());

    // Global variant change listener
    window.addEventListener('variant:change', () => this.updateWidgetContent());
    window.addEventListener('load', () => this.checkActiveOption());

    // Listen for promo events
    document.addEventListener('promoApplied', () => this.handlePromoChange());
    
    // Listen for sale prices config changes
    document.addEventListener('salePrices:updated', () => {
      // Re-apply OTP pricing when sale prices config changes
      const variantData = this.getSelectedVariantData();
      if (variantData) {
        this.updateOneTimePricing(variantData);
      }
    });
    
    // Periodic check for promo tracker availability (in case it loads after this widget)
    this.checkPromoTrackerPeriodically();
  }

  // "More you buy, more you save" tip: once on the first PDP view, then after a 1s hover.
  initBundleQuantityTip() {
    const switcher = this.querySelector('[data-bundle-quantity-switcher]');
    const tip = this.querySelector('[data-bundle-quantity-tip]');
    if (!switcher || !tip) return;

    const seenKey = 'sluggers_bundle_qty_tip_seen';
    let showTimer = null;
    let hideTimer = null;

    const hide = () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      tip.classList.remove('is-visible');
      tip.setAttribute('aria-hidden', 'true');
    };
    const show = (autoHideMs) => {
      clearTimeout(hideTimer);
      // Point the arrow at the first discounted tier (the "2" button).
      const target = this.bundleVariantButtons[1];
      if (target) {
        const center = target.getBoundingClientRect().left + target.offsetWidth / 2 - switcher.getBoundingClientRect().left;
        tip.style.setProperty('--tip-arrow-left', `${Math.round(center - 8)}px`);
      }
      tip.classList.add('is-visible');
      tip.setAttribute('aria-hidden', 'false');
      if (autoHideMs) hideTimer = setTimeout(hide, autoHideMs);
    };

    tip.querySelector('[data-bundle-quantity-tip-close]')?.addEventListener('click', hide);
    this.bundleVariantButtons.forEach(button => button.addEventListener('click', hide));

    if (window.matchMedia('(hover: hover)').matches) {
      // Short grace period on leave so the pointer can cross the gap to the close button.
      switcher.addEventListener('mouseenter', () => {
        clearTimeout(hideTimer);
        if (tip.classList.contains('is-visible')) return;
        clearTimeout(showTimer);
        showTimer = setTimeout(() => show(), 1000);
      });
      switcher.addEventListener('mouseleave', () => {
        clearTimeout(showTimer);
        hideTimer = setTimeout(hide, 300);
      });
    }

    let seen = false;
    try { seen = localStorage.getItem(seenKey) === '1'; } catch (e) {}
    if (seen || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      // The age gate may not be open yet on first paint, so wait for its cookie too.
      const ageGatePending = () =>
        !!document.querySelector('.age__popup--wrapper.pop-up--show') ||
        (!!document.querySelector('.age__popup--wrapper') && !document.cookie.split('; ').includes('age_gate_seen=1'));
      const showAfterAgeGate = () => {
        if (ageGatePending()) {
          setTimeout(showAfterAgeGate, 500);
          return;
        }
        showTimer = setTimeout(() => show(6000), 600);
        try { localStorage.setItem(seenKey, '1'); } catch (e) {}
      };
      showAfterAgeGate();
    }, { threshold: 0.6 });
    observer.observe(switcher);
  }

  initBundleQuantitySlider() {
    if (!this.bundleQuantityList || !this.bundleSliderPrev || !this.bundleSliderNext) return;

    this.boundBundleSliderPrevHandler = () => this.scrollBundleSlider(-1);
    this.boundBundleSliderNextHandler = () => this.scrollBundleSlider(1);
    this.boundBundleSliderScrollHandler = () => this.updateBundleSliderNavState();
    this.boundBundleSliderResizeHandler = () => this.updateBundleSliderNavState();

    this.bundleSliderPrev.addEventListener('click', this.boundBundleSliderPrevHandler);
    this.bundleSliderNext.addEventListener('click', this.boundBundleSliderNextHandler);
    this.bundleQuantityList.addEventListener('scroll', this.boundBundleSliderScrollHandler);
    window.addEventListener('resize', this.boundBundleSliderResizeHandler);

    this.updateBundleSliderNavState();
  }

  scrollBundleSlider(direction) {
    if (!this.bundleQuantityList) return;

    const firstButton = this.bundleVariantButtons?.[0];
    const gap = 10;
    const itemWidth = firstButton ? firstButton.offsetWidth : 84;
    const scrollDistance = (itemWidth + gap) * 2;

    this.bundleQuantityList.scrollBy({
      left: direction * scrollDistance,
      behavior: 'smooth'
    });
  }

  updateBundleSliderNavState() {
    if (!this.bundleQuantityList || !this.bundleSliderPrev || !this.bundleSliderNext) return;

    const list = this.bundleQuantityList;
    const sliderRoot = list.closest('.bundle-quantity-switcher__slider');
    const hasOverflow = list.scrollWidth > list.clientWidth + 1;
    const isAtStart = list.scrollLeft <= 8;
    const isAtEnd = list.scrollLeft >= list.scrollWidth - list.clientWidth - 8;

    this.bundleSliderPrev.disabled = !hasOverflow || isAtStart;
    this.bundleSliderNext.disabled = !hasOverflow || isAtEnd;

    if (sliderRoot) {
      sliderRoot.classList.toggle('end-scroll', !hasOverflow || isAtEnd);
    }
  }

  scrollBundleItemIntoView(button) {
    if (!button || !this.bundleQuantityList) return;

    const listRect = this.bundleQuantityList.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const isOutsideLeft = buttonRect.left < listRect.left;
    const isOutsideRight = buttonRect.right > listRect.right;

    if (isOutsideLeft || isOutsideRight) {
      button.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
    }
  }

  getBundleQuantityFromButton(button) {
    if (!button) return null;

    const datasetQuantity = Number.parseInt(button.dataset.bundleQuantity || '', 10);
    if (Number.isFinite(datasetQuantity) && datasetQuantity > 0) {
      return datasetQuantity;
    }

    const labelText = button.querySelector('.bundle-quantity-switcher__label')?.textContent?.trim() || '';
    const labelQuantity = Number.parseInt(labelText, 10);
    if (Number.isFinite(labelQuantity) && labelQuantity > 0) {
      return labelQuantity;
    }

    return null;
  }

  updateBundleQuantityUrl(quantity) {
    const parsedQuantity = Number.parseInt(String(quantity), 10);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) return;

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('quantity', String(parsedQuantity));
    nextUrl.searchParams.delete('variant');

    const nextRelativeUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
    window.history.replaceState({}, '', nextRelativeUrl);

    const shareButton = this.closest('product-info')?.querySelector('share-button');
    shareButton?.updateUrl(`${window.location.origin}${nextRelativeUrl}`);
  }

  findBundleButtonByQuantity(quantity) {
    const parsedQuantity = Number.parseInt(String(quantity), 10);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) return null;

    return this.bundleVariantButtons.find((button) => {
      if (button.disabled) return false;
      return this.getBundleQuantityFromButton(button) === parsedQuantity;
    }) || null;
  }

  applyBundleSelection(button, { shouldScroll = true, shouldSyncUrl = true } = {}) {
    if (!button?.dataset.variantId) return;

    this.bundleVariantButtons.forEach((item) => {
      item.classList.remove('bundle-quantity-switcher__item--active');
      item.setAttribute('aria-pressed', 'false');
    });

    button.classList.add('bundle-quantity-switcher__item--active');
    button.setAttribute('aria-pressed', 'true');

    this.forceOneTime = this.shouldForceOneTimeFromBundleSwitcher();
    this.applyForceOneTimeMode();
    this.updateVariantInput(button.dataset.variantId);
    this.updateSubmitAvailability(button.dataset.variantAvailable === 'true');
    this.updateOneTimePricingFromBundleOption(button);

    // Keep URL state in sync so refresh restores selected bundle quantity.
    if (shouldSyncUrl) {
      const quantity = this.getBundleQuantityFromButton(button);
      if (quantity !== null) {
        this.updateBundleQuantityUrl(quantity);
      }
    }

    if (shouldScroll) {
      this.scrollBundleItemIntoView(button);
    }

    this.updateBundleSliderNavState();
  }

  applyBundleSelectionFromUrl() {
    const quantityFromUrl = new URLSearchParams(window.location.search).get('quantity');
    if (!quantityFromUrl) return;

    const matchingButton = this.findBundleButtonByQuantity(quantityFromUrl);
    if (!matchingButton) return;

    this.applyBundleSelection(matchingButton, { shouldScroll: false, shouldSyncUrl: false });
  }

  handleBundleVariantClick(e) {
    const button = e.currentTarget;
    this.applyBundleSelection(button);
  }

  updateVariantInput(variantId) {
    const formVariantInputs = document.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"]');
    formVariantInputs.forEach((input) => {
      input.value = variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const productForm = this.closest('product-info')?.querySelector('product-form');
    if (productForm) {
      productForm.dataset.variantId = variantId;
    }
  }

  updateSubmitAvailability(isAvailable) {
    const submitButton = document.querySelector(`#ProductSubmitButton-${this.dataset.sectionId}`);
    if (!submitButton) return;

    const submitText = submitButton.querySelector('span');
    if (isAvailable) {
      submitButton.removeAttribute('disabled');
      if (submitText && window.variantStrings?.addToCart) {
        submitText.textContent = window.variantStrings.addToCart;
      }
    } else {
      submitButton.setAttribute('disabled', 'disabled');
      if (submitText && window.variantStrings?.soldOut) {
        submitText.textContent = window.variantStrings.soldOut;
      }
    }
  }

  updateOneTimePricingFromBundleOption(button) {
    let originalPriceEl = this.querySelector('.one-time-price__wrapper .original__price');
    const salePriceEl = this.querySelector('.one-time-price__wrapper .sale__price');

    if (!salePriceEl) return;

    const salePrice = Number(button.dataset.variantSalePrice || 0);
    const comparePrice = Number(button.dataset.variantComparePrice || 0);

    if (salePrice > 0) {
      salePriceEl.innerHTML = Shopify.formatMoney(salePrice);
      this.updateMvpCashValueFromPrice(salePrice);
    }

    if (comparePrice > salePrice) {
      if (!originalPriceEl) {
        originalPriceEl = document.createElement('span');
        originalPriceEl.className = 'original__price';
        salePriceEl.parentNode?.insertBefore(originalPriceEl, salePriceEl);
      }
      originalPriceEl.style.display = 'block';
      originalPriceEl.innerHTML = Shopify.formatMoney(comparePrice);
    } else if (originalPriceEl) {
      originalPriceEl.style.display = 'none';
    }

    this.updateBundleSwitcherPriceDisplay(salePrice, comparePrice);
  }

  updateBundleSwitcherPriceDisplay(salePrice, comparePrice) {
    const priceContainer = this.querySelector('[data-bundle-active-price]');
    if (!priceContainer) return;

    const saleEl = priceContainer.querySelector('.bundle-quantity-switcher__price-sale');
    let originalEl = priceContainer.querySelector('.bundle-quantity-switcher__price-original');

    if (saleEl && salePrice > 0) {
      saleEl.textContent = Shopify.formatMoney(salePrice);
    }

    if (comparePrice > salePrice) {
      if (!originalEl) {
        originalEl = document.createElement('span');
        originalEl.className = 'bundle-quantity-switcher__price-original';
        priceContainer.prepend(originalEl);
      }
      originalEl.textContent = Shopify.formatMoney(comparePrice);
    } else if (originalEl) {
      originalEl.remove();
    }
  }

  updateMvpCashValueFromPrice(priceCents) {
    const normalizedPriceCents = Number(priceCents);
    if (!Number.isFinite(normalizedPriceCents) || normalizedPriceCents <= 0) return;

    const mvpCashAmount = (normalizedPriceCents * 0.001).toFixed(1);
    const mvpCashTargets = this.querySelectorAll('[data-mvp-cash-target][data-mvp-cash-template]');

    mvpCashTargets.forEach((target) => {
      const template = target.dataset.mvpCashTemplate;
      if (!template || !template.includes('$0')) return;

      target.innerHTML = template.split('$0').join(`<strong>${mvpCashAmount}</strong>`);
    });
  }

  handlePlanClick(e) {
    const button = e.target;
    
    // Update active state
    this.planButtons.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');
    
    // Select subscription option
    this.subscribeOption.checked = true;
    
    // Update selling plan
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = button.dataset.planId;
    }
    
    this.checkActiveOption();
  }

  checkActiveOption() {
    if (!this.subscribeOption || !this.oneTimeOption || !this.sellingPlanInput) return;

    if (this.forceOneTime) {
      this.applyForceOneTimeMode();
      this.updateWidgetContent();
      return;
    }

    if (this.subscribeOption.checked) {
      const activeButton = this.querySelector('.subscription__plan-button.active');
      if (activeButton) {
        this.sellingPlanInput.value = activeButton.dataset.planId;
      }
    } else if (this.oneTimeOption.checked) {
      this.sellingPlanInput.value = '';
    }

    this.updateWidgetContent();
  }

  updateWidgetContent() {
    try {
      const variantData = this.getSelectedVariantData();
      if (!variantData) return;

      const activePlanPosition = this.getActivePlanPosition();
      this.applyForceOneTimeMode();
      
      this.updateOneTimePricing(variantData);
      this.updateSubscriptionPricing(variantData, activePlanPosition);
      
      // Re-apply sale prices if enabled (to ensure Liquid-rendered prices are preserved)
      if (window.salePricesConfig && window.salePricesConfig.enabled) {
        // Force re-render to ensure original__price is shown
        requestAnimationFrame(() => {
          this.updateSubscriptionPricing(variantData, activePlanPosition);
        });
      }
    } catch (error) {
      console.warn('YotpoSubscriptionWidget: Error updating content', error);
    }
  }

  getSelectedVariantData() {
    const variantElement = document.querySelector('.product [data-selected-variant]');
    return variantElement ? JSON.parse(variantElement.innerHTML) : null;
  }

  getActivePlanPosition() {
    const activeButton = this.querySelector('.subscription__plan-button.active');
    return activeButton ? Number(activeButton.dataset.optionPosition) : 0;
  }

  updateOneTimePricing(variant) {
    const originalPriceEl = this.querySelector('.one-time-price__wrapper .original__price');
    const salePriceEl = this.querySelector('.one-time-price__wrapper .sale__price');
    
    if (!originalPriceEl || !salePriceEl) return;

    // Check if sale prices module is enabled
    const salePricesEnabled = window.salePricesConfig && window.salePricesConfig.enabled;
    
    if (salePricesEnabled) {
      // Apply sale price logic for OTP
      this.applyOTPSalePrice(variant, originalPriceEl, salePriceEl);
      const discountPercent = window.salePricesConfig.discountPercent || 40;
      const basePrice = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
      const discountMultiplier = 1 - (discountPercent / 100);
      const salePriceCents = Math.round(basePrice * discountMultiplier);
      this.updateMvpCashValueFromPrice(salePriceCents);
    } else if (Number(this.dataset.superSwitchPromoDiscount) > 0) {
      const discountPercent = Number(this.dataset.superSwitchPromoDiscount);
      const salePriceCents = Math.round(variant.price * (1 - discountPercent / 100));
      originalPriceEl.style.display = 'block';
      originalPriceEl.innerHTML = Shopify.formatMoney(variant.price);
      salePriceEl.innerHTML = Shopify.formatMoney(salePriceCents);
      this.updateMvpCashValueFromPrice(salePriceCents);
    } else {
      // Use standard variant pricing (no sale price override)
      // Clean up sale price flags and styling
      delete salePriceEl.dataset.salePriceProcessed;
      const priceWrapper = salePriceEl.closest('.one-time-price__wrapper');
      if (priceWrapper) {
        priceWrapper.classList.remove('otp-price--sale-applied');
      }
      
      const hasDiscount = variant.price < variant.compare_at_price;
      originalPriceEl.style.display = hasDiscount ? 'block' : 'none';
      
      salePriceEl.innerHTML = Shopify.formatMoney(variant.price);
      originalPriceEl.innerHTML = Shopify.formatMoney(variant.compare_at_price);
      this.updateMvpCashValueFromPrice(variant.price);
    }
  }

  /**
   * Apply sale price to one-time purchase option
   * Calculates sale price based on salePrices module configuration
   */
  applyOTPSalePrice(variant, originalPriceEl, salePriceEl) {
    // Get discount percent from sale prices module (default 40%)
    const discountPercent = window.salePricesConfig.discountPercent || 40;
    
    // Determine base price: use compare_at_price if available, otherwise use regular price
    const basePrice = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
    
    // Calculate sale price
    const discountMultiplier = 1 - (discountPercent / 100);
    const salePriceCents = Math.round(basePrice * discountMultiplier);
    
    // Format prices
    const formattedSalePrice = Shopify.formatMoney(salePriceCents);
    const formattedOriginalPrice = Shopify.formatMoney(basePrice);
    
    // Update sale price element
    salePriceEl.innerHTML = formattedSalePrice;
    
    // Always show original price when sale prices are enabled
    originalPriceEl.style.display = 'block';
    originalPriceEl.innerHTML = formattedOriginalPrice;
    
    // Mark as processed to prevent sale-prices.js from double-processing
    salePriceEl.dataset.salePriceProcessed = 'true';
    
    // Add sale styling class
    const priceWrapper = salePriceEl.closest('.one-time-price__wrapper');
    if (priceWrapper) {
      priceWrapper.classList.add('otp-price--sale-applied');
    }
  }

  updateSubscriptionPricing(variant, planPosition) {
    const originalPriceEl = this.querySelector('.subscription-price__wrapper .original__price');
    const salePriceEl = this.querySelector('.subscription-price__wrapper .sale__price');
    
    if (!originalPriceEl || !salePriceEl || !variant.selling_plan_allocations) return;

    // If prices are Liquid-rendered with sale prices, preserve them
    if (originalPriceEl.dataset.liquidRendered === 'true' || salePriceEl.dataset.liquidRendered === 'true') {
      // Ensure original__price is visible if it exists
      if (originalPriceEl.textContent.trim()) {
        originalPriceEl.style.display = 'block';
      }
      return; // Don't overwrite Liquid-rendered prices
    }

    const allocation = variant.selling_plan_allocations[planPosition];
    if (!allocation) return;

    // Check if sale prices module is enabled
    const salePricesEnabled = window.salePricesConfig && window.salePricesConfig.enabled;
    
    if (salePricesEnabled) {
      // Apply sale price logic for subscription pricing
      this.applySubscriptionSalePrice(allocation, originalPriceEl, salePriceEl);
    } else {
      // Use standard subscription pricing
      salePriceEl.innerHTML = Shopify.formatMoney(allocation.price);
      const hasDiscount = allocation.price < allocation.compare_at_price;
      originalPriceEl.style.display = hasDiscount ? 'block' : 'none';
      originalPriceEl.innerHTML = Shopify.formatMoney(allocation.compare_at_price);
    }
  }

  /**
   * Apply sale price to subscription pricing
   * Calculates sale price based on salePrices module configuration
   */
  applySubscriptionSalePrice(allocation, originalPriceEl, salePriceEl) {
    // Get discount percent from sale prices module (default 40%)
    const discountPercent = window.salePricesConfig.discountPercent || 40;
    
    // Determine base price: use compare_at_price if available, otherwise calculate from subscription price
    let basePrice = allocation.compare_at_price;
    if (!basePrice || basePrice === 0) {
      // Calculate compare_at_price from subscription price using reverse discount
      const discountMultiplier = 1 - (discountPercent / 100);
      basePrice = Math.round(allocation.price / discountMultiplier);
    }
    
    // Calculate sale price from compare_at_price
    const discountMultiplier = 1 - (discountPercent / 100);
    const salePriceCents = Math.round(basePrice * discountMultiplier);
    
    // Format prices
    const formattedSalePrice = Shopify.formatMoney(salePriceCents);
    const formattedOriginalPrice = Shopify.formatMoney(basePrice);
    
    // Update prices
    salePriceEl.innerHTML = formattedSalePrice;
    
    // Always show original price when sale prices are enabled
    originalPriceEl.style.display = 'block';
    originalPriceEl.innerHTML = formattedOriginalPrice;
    
    // Mark as processed to prevent sale-prices.js from modifying
    salePriceEl.dataset.salePriceProcessed = 'true';
    originalPriceEl.dataset.salePriceProcessed = 'true';
  }

  // Public method to hide widget via cookie
  hideWidget() {
    document.cookie = `${this.hideCookieName}=true; path=/; max-age=31536000`; // 1 year
    this.style.display = 'none';
    
    // Select one-time option if available
    if (this.oneTimeOption) {
      this.oneTimeOption.checked = true;
      this.checkActiveOption();
    }
  }

  // Public method to show widget
  showWidget() {
    document.cookie = `${this.hideCookieName}=false; path=/; max-age=31536000`;
    this.style.display = 'block';
  }

  handlePromoChange() {
    // When promo status changes, update selection but keep widget visible
    if (this.hasActivePromo()) {
      this.selectOneTimeOption();
    }
  }

  checkPromoTrackerPeriodically() {
    // Check for promo tracker availability every 500ms for up to 5 seconds
    let attempts = 0;
    const maxAttempts = 10;
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (this.hasActivePromo()) {
        // Promo tracker became available and has active promo - select one-time
        this.handlePromoChange();
        clearInterval(checkInterval);
      } else if (attempts >= maxAttempts) {
        // Stop checking after max attempts
        clearInterval(checkInterval);
      }
    }, 500);
  }
}

// Register the custom element
if (!customElements.get('yotpo-subscription-widget')) {
  customElements.define('yotpo-subscription-widget', YotpoSubscriptionWidget);
}
})();
